import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
} from "react-icons/fi";
import "../styles/ResetPassword.css";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [tenantCode, setTenantCode] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const resetToken = urlParams.get("token");
    const linkTenantCode = urlParams.get("tenant");
    if (!resetToken) {
      toast.error("Invalid reset link. Please request a new one.");
      navigate("/forgot-password");
    } else {
      setToken(resetToken);
      setTenantCode(linkTenantCode || "");
    }
  }, [location, navigate]);

  // Calculate password strength
  const calculateStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 6) strength++;
    if (pass.length >= 10) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    calculateStrength(newPassword);
    setError("");
  };

  const getStrengthText = () => {
    if (passwordStrength === 0) return "";
    if (passwordStrength <= 2) return "Weak";
    if (passwordStrength <= 3) return "Fair";
    if (passwordStrength <= 4) return "Good";
    return "Strong";
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 2) return "#dc3545";
    if (passwordStrength <= 3) return "#ffc107";
    if (passwordStrength <= 4) return "#17a2b8";
    return "#28a745";
  };

  const getStrengthWidth = () => {
    if (passwordStrength === 0) return "0%";
    return `${(passwordStrength / 5) * 100}%`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.post("/auth/reset-password", {
        token,
        newPassword: password,
        tenantCode,
      });

      if (response.data.success) {
        setResetSuccess(true);
        toast.success("Password reset successfully!");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(response.data.message || "Failed to reset password");
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to reset password. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (resetSuccess) {
    return (
      <div className="rp-container">
        <div className="rp-card">
          <div className="rp-success-icon">
            <FiCheckCircle />
          </div>
          <h1 className="rp-success-title">Password Reset Successful!</h1>
          <p className="rp-success-message">
            Your password has been changed successfully.
          </p>
          <p className="rp-redirect-message">Redirecting you to login...</p>
          <Link to="/login" className="rp-login-link">
            Go to Login →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-container">
      <div className="rp-card">
        <div className="rp-header">
          <div className="rp-icon">
            <FiLock />
          </div>
          <h1>Create New Password</h1>
          <p>Please enter your new password below.</p>
        </div>

        <form className="rp-form" onSubmit={handleSubmit}>
          <div className="rp-form-group">
            <label>New Password</label>
            <div className="rp-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                required
                placeholder="Enter new password (min. 6 characters)"
                className="rp-input"
              />
              <button
                type="button"
                className="rp-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className="rp-strength-container">
                <div className="rp-strength-bar">
                  <div
                    className="rp-strength-fill"
                    style={{
                      width: getStrengthWidth(),
                      backgroundColor: getStrengthColor(),
                    }}
                  />
                </div>
                <div
                  className="rp-strength-text"
                  style={{ color: getStrengthColor() }}
                >
                  {getStrengthText()}
                </div>
              </div>
            )}
          </div>

          <div className="rp-form-group">
            <label>Confirm New Password</label>
            <div className="rp-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                className="rp-input"
              />
              <button
                type="button"
                className="rp-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div className="rp-error-message">
                <FiAlertCircle />
                <span>Passwords do not match</span>
              </div>
            )}
          </div>

          {error && (
            <div className="rp-error-box">
              <FiAlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="rp-password-requirements">
            <p>Password must contain:</p>
            <ul>
              <li className={password.length >= 6 ? "valid" : ""}>
                ✓ At least 6 characters
              </li>
              <li className={/[A-Z]/.test(password) ? "valid" : ""}>
                ✓ At least one uppercase letter
              </li>
              <li className={/[0-9]/.test(password) ? "valid" : ""}>
                ✓ At least one number
              </li>
              <li className={/[^A-Za-z0-9]/.test(password) ? "valid" : ""}>
                ✓ At least one special character
              </li>
            </ul>
          </div>

          <button
            type="submit"
            className="rp-submit-btn"
            disabled={
              loading ||
              !password ||
              !confirmPassword ||
              password !== confirmPassword
            }
          >
            {loading ? "Resetting Password..." : "Reset Password"}
          </button>
        </form>

        <div className="rp-footer">
          <Link to="/login" className="rp-back-link">
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
